/* hs-eslint ignored failing-rules */

/* eslint-disable react/prop-types */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { connect } from 'react-redux';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIModal from 'UIComponents/dialog/UIModal';
import { EntityTypes, NETWORKS_WITH_ALL } from 'AdsUI/lib/Constants';
import { setColumns } from 'AdsUI/actions/ColumnActions';
import I18n from 'I18n';
import { getSelectedAndPossibleColumns, getColumnsRequestStatus, getColumnDisplayLabels } from 'AdsUI/selectors/ColumnsSelectors';
import { hasAccessToRoiReporting, hasWriteScope, getHubSpotUserId } from 'AdsUI/selectors/AuthSelectors';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import makeLoadable from 'AdsUI/lib/makeLoadable';
import H2 from 'UIComponents/elements/headings/H2';
import { fetchAdsUserAttributes } from 'AdsUI/actions/UserAttributesActions';
import { getRequestStatus } from 'AdsUI/selectors/RequestStatusSelectors';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { getLifecycleStageLabelsByStageId } from 'AdsUI/selectors/LifecycleStageSelectors';

const trackEvent = (network, entityType, action, {
  added,
  column,
  removed,
  selected
} = {}) => usageTracker.track('changeColumnOrder', {
  grouping: entityType,
  network,
  action,
  column,
  selected,
  added,
  removed
});

const saveColumns = (entityType, setColumnsAction, network) => newColumns => {
  setColumnsAction(entityType, network, newColumns);
};

const EditColumnsModal = makeLoadable(() => import('AdsUI/components/EditColumnsModal'));

const EditColumnsContainer = (_ref) => {
  let {
    onClose,
    network,
    entityType,
    accessToRoiReporting,
    writeScope,
    columnOrderRequestStatus,
    userAttributesFetched,
    userId,
    columns: {
      allowed,
      selected
    },
    lifecycleStageLabelsByStageId,
    baseColumnDisplayLabels
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["onClose", "network", "entityType", "accessToRoiReporting", "writeScope", "columnOrderRequestStatus", "userAttributesFetched", "userId", "columns", "lifecycleStageLabelsByStageId", "baseColumnDisplayLabels"]);

  const columnDisplayLabels = useMemo(() => Object.keys(baseColumnDisplayLabels).reduce((displayLabels, col) => {
    const labelKey = `campaigns.editColumnsLabels.${col}`;

    if (isValidI18nKey(labelKey)) {
      displayLabels[col] = I18n.text(labelKey);
    } else {
      displayLabels[col] = baseColumnDisplayLabels[col];
    }

    return displayLabels;
  }, {}), [baseColumnDisplayLabels]);
  const fetchAllUserAttributes = props.fetchAdsUserAttributes;
  useEffect(() => {
    if (!userAttributesFetched.fetched && !userAttributesFetched.fetching) {
      fetchAllUserAttributes(userId);
    }
  }, [fetchAllUserAttributes, userId, userAttributesFetched]);
  const possibleColumns = useMemo(() => allowed.sort((a, b) => columnDisplayLabels[a] > columnDisplayLabels[b] ? 1 : -1).toArray(), [allowed, columnDisplayLabels]);
  const selectedColumns = useMemo(() => selected.toArray(), [selected]);
  return /*#__PURE__*/_jsxs(UIModal, {
    size: "auto",
    width: 770,
    "data-test-id": "edit-columns-modal",
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editColumnsModal.title"
        })
      })]
    }), columnOrderRequestStatus.fetched ? /*#__PURE__*/_jsx(EditColumnsModal, {
      possibleColumns: possibleColumns,
      selectedColumns: selectedColumns,
      onSave: saveColumns(entityType, props.setColumns, network),
      onClose: onClose,
      trackEvent: trackEvent.bind(undefined, network, entityType),
      hasAccessToRoiReporting: accessToRoiReporting,
      hasWriteScope: writeScope,
      columnDisplayLabels: columnDisplayLabels,
      lifecycleStageLabelsByStageId: lifecycleStageLabelsByStageId
    }) : /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {})
    })]
  });
};

EditColumnsContainer.propTypes = {
  onClose: PropTypes.func.isRequired,
  network: PropTypes.oneOf(NETWORKS_WITH_ALL).isRequired,
  entityType: PropTypes.oneOf(Object.values(EntityTypes))
};

const mapStateToProps = (state, props) => ({
  columns: getSelectedAndPossibleColumns(state, props),
  lifecycleStageLabelsByStageId: getLifecycleStageLabelsByStageId(state),
  accessToRoiReporting: hasAccessToRoiReporting(state),
  writeScope: hasWriteScope(state),
  columnOrderRequestStatus: getColumnsRequestStatus(state),
  userId: getHubSpotUserId(state),
  userAttributesFetched: getRequestStatus('userAttributes')(state),
  baseColumnDisplayLabels: getColumnDisplayLabels(state)
});

export default connect(mapStateToProps, {
  setColumns,
  fetchAdsUserAttributes
})(EditColumnsContainer);